import React from "react";
import { Play, Pause, Volume2, Download } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AudioPlayerBar() {
  const { track, playing, progress, duration, previewMode, toggle, seek } = usePlayer();
  const { user, refresh } = useAuth();

  if (!track) return null;

  const cap = previewMode ? Math.min(30, duration || 30) : duration || 0;
  const pct = cap ? Math.min(100, (progress / cap) * 100) : 0;

  const handleDownload = async () => {
    if (!user) return toast.error("Sign in to download");
    try {
      const { data } = await api.post(`/tracks/${track.id}/download`);
      window.open(data.download_url, "_blank");
      toast.success(data.already_owned ? "Track already owned" : "Track unlocked! Download starting.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-black/85 border-t border-white/10 shadow-[0_-8px_32px_rgba(0,0,0,0.6)]" data-testid="audio-player-bar">
      <div className="container-x py-3 flex items-center gap-4">
        <img src={track.cover_url} alt="" className="w-14 h-14 object-cover flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold truncate">{track.title}</span>
            {previewMode && (
              <span className="hidden sm:inline text-[9px] font-mono-plex uppercase tracking-widest text-[#B3FF00] border border-[#B3FF00]/30 px-1.5 py-0.5">
                30s Preview
              </span>
            )}
          </div>
          <div className="text-xs text-white/50 font-mono-plex">{track.genre}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] text-white/40 font-mono-plex w-8">{fmt(progress)}</span>
            <div
              className="flex-1 h-1 bg-white/10 cursor-pointer group"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                seek((e.clientX - rect.left) / rect.width);
              }}
              data-testid="player-progress-bar"
            >
              <div className="h-full bg-[#B3FF00] group-hover:bg-[#FF5E00] transition-colors" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-white/40 font-mono-plex w-8">{fmt(cap)}</span>
          </div>
        </div>
        <button onClick={toggle} className="w-12 h-12 bg-[#FF5E00] hover:bg-[#FF7A33] flex items-center justify-center transition-colors" data-testid="player-toggle-btn">
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <button onClick={handleDownload} className="hidden sm:flex w-12 h-12 border border-white/10 hover:border-[#B3FF00] items-center justify-center transition-colors" title="Download full track" data-testid="player-download-btn">
          <Download className="w-4 h-4" />
        </button>
        <Volume2 className="hidden md:block w-4 h-4 text-white/40" />
      </div>
    </div>
  );
}
